import React, { forwardRef } from 'react';
import { formatPaise } from '../lib/cn';
import { cn } from '../lib/cn';
import { ArrowUp } from 'lucide-react';

export interface MarginIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
  wholesalePaise: number;
  mrpPaise: number;
  marginPercent?: number;
  gstPercent?: number;
  compact?: boolean;
}

export const MarginIndicator = forwardRef<HTMLDivElement, MarginIndicatorProps>(
  ({ wholesalePaise, mrpPaise, marginPercent: explicitMargin, gstPercent, compact = false, className, ...props }, ref) => {
    const gstRate = gstPercent !== undefined && gstPercent !== null ? gstPercent : 5;
    const gstMultiplier = 1 + gstRate / 100;

    // MDD Margin in ₹/kg (considering dynamic GST if calculated from MRP & Wholesale)
    const mddMarginPerKg = explicitMargin !== undefined && explicitMargin > 0
      ? explicitMargin
      : mrpPaise > 0 && wholesalePaise > 0
        ? parseFloat(Math.max(0, (mrpPaise / gstMultiplier - wholesalePaise) / 100).toFixed(2))
        : 0;

    const marginPaise = Math.round(mddMarginPerKg * 100);
    const gstPaise = Math.round((wholesalePaise + marginPaise) * (gstRate / 100));
    const formattedMargin = Number(mddMarginPerKg).toFixed(2).replace(/\.00$/, "");

    let colorClass = "bg-success/10 text-success border-success/20";
    
    if (mddMarginPerKg < 1) {
      colorClass = "bg-destructive/10 text-destructive border-destructive/20";
    } else if (mddMarginPerKg < 2) {
      colorClass = "bg-amber-500/10 text-amber-600 dark:text-amber-500 border-amber-500/20";
    }

    if (compact) {
      return (
        <span 
          ref={ref}
          className={cn("inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium border", colorClass, className)}
          title={`MDD Margin: ₹${formattedMargin}/kg | GST: ${gstRate}%`}
          {...props}
        >
          <ArrowUp className="w-3 h-3 mr-0.5" />
          ₹{formattedMargin}/kg
        </span>
      );
    }

    return (
      <div 
        ref={ref}
        className={cn("flex flex-wrap items-center gap-2 text-sm", className)}
        {...props}
      >
        <div className="flex items-center gap-2 text-muted-foreground text-xs">
          <span>Trade: <span className="font-semibold text-foreground">{formatPaise(wholesalePaise)}</span></span>
          <span className="text-border">|</span>
          <span>GST: <span className="font-semibold text-foreground">{gstRate}% ({formatPaise(gstPaise)})</span></span>
          <span className="text-border">|</span>
          <span>MRP: <span className="font-semibold text-foreground">{formatPaise(mrpPaise)}</span></span>
          <span className="text-border">|</span>
        </div>
        
        <span className={cn("inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium border", colorClass)}>
          MDD Margin: ₹{formattedMargin}/kg (+{formatPaise(marginPaise)})
        </span>
      </div>
    );
  }
);

MarginIndicator.displayName = 'MarginIndicator';
